import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { Panel, Button } from '../../../../components';
import type { MailboxError, MailboxRecord } from '../../../../mock-data/mailbox-records';
import { mailboxEmail } from '../../../../mock-data/mailbox-records';
import { generateMailboxPassword } from '../../password';
import {
  validateMailboxLocalPart,
  validateMailboxPassword,
  MAILBOX_RESERVED_LOCAL_PARTS,
  type MailboxLocalPartError,
  type MailboxPriceView,
} from '../../state';
import { CredentialBlock } from '../../detail/components';
import { AddressField } from '../AddressField';
import { DismissibleNotice } from '../DismissibleNotice';
import { mailboxErrorText } from '../../errors';
import { FieldWrap, FieldError } from '../FieldRow/FieldRow.styles';
import { PasswordEntry } from '../PasswordEntry';
import { Form, Header, FormTitle, Body, FooterRow } from './CreateMailboxView.styles';

export type CreateMailboxViewProps = {
  /** The brand's fixed mail domain, shown as the field suffix (`hello@{domain}`). */
  domain: string;
  /** All current mailboxes, for the duplicate guard rail. */
  mailboxes: readonly MailboxRecord[];
  /** Whether THIS mailbox takes a paid slot (drives the heading, the banner and where submit goes
   *  next). */
  paid: boolean;
  /** What a paid mailbox costs, as the service answered (see `mailboxPriceView`): the banner's
   *  figure. No number until it is `priced`; `free` (charging switched off) says so instead. */
  price: MailboxPriceView;
  /** True while the (simulated) create is in flight, or while the checkout sheet is open over it. */
  submitting: boolean;
  /**
   * The validated address (the full, lowercase email: the local part is this form's concern and
   * stops here) and the password, generated or typed, which is always sent. Validation runs HERE, so
   * the parent trusts both values: the name passed the guard rails, the password meets the service's
   * bounds. `typed` says whether the operator typed that password (their own) rather than keeping a
   * generated one, so the parent can seed a returning form exactly as it was.
   */
  onSubmit: (email: string, password: string, typed: boolean) => void;
  /** Cancel: return to the grid without creating. */
  onClose: () => void;
  /**
   * The server refused the last submit: the address it refused, and the service's error (`null`
   * when the service did not answer at all). Its `message` is shown VERBATIM: a `400` against the
   * password field when the refused submit carried a password the operator TYPED (the provider's own
   * strength rules, which this form deliberately does not guess at), anything else against the
   * address. Cleared by the parent on `onEdited`.
   */
  refusal?: { email: string; error: MailboxError | null } | null;
  /** The operator changed a field: any refusal no longer describes what is on screen. */
  onEdited?: () => void;
  /** A `409`'s way forward: rotate the existing mailbox's password (the parent confirms first). */
  onRotate?: () => void;
  /** A `402`'s way forward: add funds to the main wallet, where the rest of the app does it. Absent,
   *  the refusal is said with no link. */
  onTopUp?: () => void;
  /** True while that rotation is in flight. */
  rotating?: boolean;
  /** The rotation's answer: the new password, shown once, and when it was set. */
  rotated?: { password: string; setAt: string } | null;
  /**
   * Seeds for a form the section brings BACK after a refused create (the provisioning screen had
   * replaced it, so nothing was kept mounted): the local part and the password that were submitted,
   * and whether that password was typed. Read on mount only. With `refusal` (keyed by the address),
   * the operator is put back where they were, the refusal where it rendered.
   */
  initialLocalPart?: string;
  initialPassword?: string;
  initialPasswordTyped?: boolean;
};

/** Nothing to do: the rotated password cannot be "generated" a first time, it already exists. */
const noop = (): void => undefined;

/** Map a local-part rejection to its specific message (English copy is the i18n key). `reserved`
 *  says WHICH names are reserved rather than just refusing. */
const localPartMessage = (reason: MailboxLocalPartError, t: TFunction): string => {
  switch (reason) {
    case 'empty':
      return t('Enter the part before the @ sign.');
    case 'invalid':
      return t('Use letters, numbers, dots or dashes only.');
    case 'crm':
      return t("That name is reserved for the casino's CRM mail. Pick another one.");
    case 'reserved':
      return t('That name is reserved for the mail system ({{names}}). Pick another one.', {
        names: MAILBOX_RESERVED_LOCAL_PARTS.join(', '),
      });
    case 'duplicate':
      return t('You already have that mailbox.');
    default: {
      const unhandled: never = reason;
      return unhandled;
    }
  }
};

/**
 * Create a mailbox on the brand domain. A view swap inside the section (not a route), so leaving
 * unmounts it; Cancel returns to the grid. Two fields: the local part (the domain is fixed and shown
 * as the suffix) and the password.
 *
 * Validation runs HERE and is the single validation site, so a rejected name never reaches the
 * parent: paying and then being told the name is taken would be a refund, so the duplicate and
 * reserved guards fire BEFORE any invoice is minted. The parent trusts the submitted value.
 *
 * The password field is PREFILLED with a generated password (`password.ts`), MASKED by default with
 * the eye as the row's leading control, then Copy right after it and Regenerate at the far right:
 * the operator sees and copies the password here, before the mailbox exists (the provisioning screen
 * does not repeat it). It is always sent. An operator who wants their own overwrites it; the strength meter
 * appears only then (a generated value always scores strong). Whatever is in the field gets the
 * service's own bounds (8 to 54, no whitespace at either end) here. The provider's strength rules
 * are NOT checked (and cannot be relied on: it accepted `123456789A`); its `400` is shown verbatim.
 *
 * A `409` means the address already exists at the provider (the list here was stale). A create is
 * never answered with a new password, and it is never retried: the form shows the service's
 * message and offers to ROTATE that mailbox's password instead, then shows the new one once.
 *
 * A `402` means the main wallet could not pay for a paid create: NOTHING was created and nothing
 * charged. The form says so in our words (see `mailboxErrorText`), offers Add funds, and leaves
 * Create enabled: after a top-up the same create goes through, the opposite of a `409`.
 *
 * What the mailbox costs is said ONCE, in a banner under the title: green when this one is included
 * (and what the next will cost) or when charging is switched off, amber when it is paid (the
 * service's price, charged once, at creation). Before the price has arrived, or when its read
 * failed, the banner says the same without a number. The banner is dismissible for THIS visit only, never permanently: it is the one
 * place the price appears on this form, and a remembered dismissal would let an operator hide it
 * from themselves with no way back. The form remounts on every visit, so nothing is stored.
 *
 * The form stays MOUNTED under the checkout sheet (a portal), so closing the sheet returns to it
 * with the typed address and password intact; nothing is lifted into the parent for that. A brand's
 * FIRST create is different: the provisioning screen replaces the form on the click, so a refusal
 * brings back a NEW form, seeded by the section (`initial*`) from what was submitted.
 */
export const CreateMailboxView = ({
  domain,
  mailboxes,
  paid,
  price,
  submitting,
  onSubmit,
  onClose,
  refusal = null,
  onEdited,
  onRotate,
  onTopUp,
  rotating = false,
  rotated = null,
  initialLocalPart = '',
  initialPassword,
  initialPasswordTyped = false,
}: CreateMailboxViewProps): React.ReactElement => {
  const { t } = useTranslation();
  const priced = price.kind === 'priced' ? price.label : null;

  const [dismissed, setDismissed] = React.useState(false);
  const onDismissBanner = React.useCallback(() => setDismissed(true), []);

  const [localPart, setLocalPart] = React.useState(initialLocalPart);
  const [error, setError] = React.useState<string | null>(null);
  // PREFILLED with a generated password, copyable, and always sent: the operator can see it (the eye)
  // and copy it before committing (an empty "the service will generate one" field said nothing, and
  // the first operator typed a weak one the provider accepted). Overwrite it to choose your own.
  const [password, setPassword] = React.useState(() => initialPassword ?? generateMailboxPassword());
  // Whether the operator has typed into the password field. A generated value (initial, or after
  // Regenerate) is not an edit, so the strength meter stays hidden for it: a generated value always
  // scores strong, which is noise; the moment they type their own, it is the thing that matters.
  const [edited, setEdited] = React.useState(initialPasswordTyped);
  // The service's bounds (8 to 54, no edge whitespace), for whatever is in the field.
  const passwordError = validateMailboxPassword(password);
  const passwordReady = passwordError === null;
  // Whether the last submit carried a password the operator typed (their own), rather than the
  // generated one. Recorded AT submit, so typing afterwards cannot move or clear a refusal.
  // A seeded form's last submit was the refused one, so it starts as that submit's.
  const [typedAtSubmit, setTypedAtSubmit] = React.useState(initialPasswordTyped);
  // Where the server's refusal is shown. A 400 is about the password only when the operator typed it:
  // a generated password is ours and strong, so a 400 on it is about the address or the request (an
  // address off the domain, a missing registration) and shows with the address, like anything else.
  // Decided by what was sent, never by reading the message.
  const refusedPassword =
    refusal?.error?.code === 'invalid_request' && typedAtSubmit ? refusal.error.message : null;
  const conflict = refusal?.error?.code === 'conflict' ? refusal : null;
  // A 402: the main wallet was short. Its own block (with Add funds), never the generic line.
  const insufficient = refusal?.error?.code === 'insufficient_credits' ? refusal : null;
  // The 409 gate holds while the typed address IS the refused one: that address would be refused
  // again. Another address is a new create. (After a rotation the refusal stays on screen, with
  // the password shown once, so the gate must not outlive the address it is about.)
  const conflictHolds = conflict !== null && mailboxEmail(localPart, domain) === conflict.email;
  const refusedOther =
    refusal && !refusedPassword && !conflict && !insufficient
      ? mailboxErrorText(refusal.error, t, t('The mailbox could not be created. Try again in a moment.'))
      : null;

  const onChange = React.useCallback(
    (value: string) => {
      setLocalPart(value);
      setError(null);
      onEdited?.();
    },
    [onEdited],
  );
  // A password edit makes only a PASSWORD refusal (a 400) stale. A 409 is about the address: it
  // stands, with its rotation offer and its submit gate, until the address changes.
  const onPasswordChange = React.useCallback(
    (value: string) => {
      setPassword(value);
      setEdited(true);
      if (refusedPassword) onEdited?.();
    },
    [refusedPassword, onEdited],
  );
  const regenerate = React.useCallback(() => {
    setPassword(generateMailboxPassword());
    setEdited(false);
    if (refusedPassword) onEdited?.();
  }, [refusedPassword, onEdited]);

  const submit = React.useCallback(() => {
    // The gate lives in the handler as well as on the button: a non-native control's disabled
    // styling alone does not block a click.
    // A 409 is never retried: the same address would be refused again.
    if (submitting || !passwordReady || conflictHolds) return;
    const reason = validateMailboxLocalPart(localPart, mailboxes, domain);
    if (reason) {
      setError(localPartMessage(reason, t));
      return;
    }
    setError(null);
    setTypedAtSubmit(edited);
    onSubmit(mailboxEmail(localPart, domain), password, edited);
  }, [submitting, passwordReady, conflictHolds, localPart, mailboxes, domain, onSubmit, password, edited, t]);

  return (
    <Panel>
      <Form data-testid="mailbox-create-form">
        <Header>
          {/* With charging switched off a paid slot costs nothing, so it is not called paid. */}
          {paid && price.kind === 'free' ? (
            <FormTitle title={t('Create a mailbox')} />
          ) : paid ? (
            <FormTitle title={t('Create a paid mailbox')} />
          ) : (
            <FormTitle title={t('Create your free mailbox')} />
          )}
          {/* The extractor only sees string-literal `text` / `title` attributes, so each branch is a
              literal. */}
          {dismissed ? null : paid && priced ? (
            <DismissibleNotice
              tone="warning"
              icon="lock"
              title="This mailbox costs {{price}}."
              titlePlaceholders={{ price: priced }}
              text="Charged once, when it is created. You confirm on the next step before anything is charged."
              onDismiss={onDismissBanner}
            />
          ) : paid && price.kind === 'free' ? (
            <DismissibleNotice
              tone="success"
              title="This mailbox is free of charge."
              text="Nothing is charged when it is created."
              onDismiss={onDismissBanner}
            />
          ) : paid ? (
            <DismissibleNotice
              tone="warning"
              icon="lock"
              title="This is a paid mailbox."
              text="Nothing is charged before you confirm on the next step."
              onDismiss={onDismissBanner}
            />
          ) : priced ? (
            <DismissibleNotice
              tone="success"
              title="This one is free, forever."
              text="Every one after it costs {{price}}, charged once."
              placeholders={{ price: priced }}
              onDismiss={onDismissBanner}
            />
          ) : price.kind === 'free' ? (
            <DismissibleNotice
              tone="success"
              title="This one is free, forever."
              text="The ones after it are free of charge too."
              onDismiss={onDismissBanner}
            />
          ) : (
            <DismissibleNotice
              tone="success"
              title="This one is free, forever."
              text="Every one after it is a paid mailbox."
              onDismiss={onDismissBanner}
            />
          )}
        </Header>

        <Body>
          <FieldWrap>
            <AddressField
              label={t('Mailbox address')}
              value={localPart}
              domain={domain}
              placeholder={t('hello')}
              onChange={onChange}
              disabled={submitting}
              maxLength={64}
            />
            {error ? <FieldError text={error} tag="span" /> : null}
            {/* The service's words, verbatim: they say what is wrong better than a generic line. */}
            {refusedOther ? <FieldError text={refusedOther} tag="span" /> : null}
          </FieldWrap>

          {/* THE 402: the main wallet could not pay. Nothing exists and nothing was charged, so the
              same create is allowed again (no gate), once funds are added where the app adds them. */}
          {insufficient ? (
            <FieldWrap data-testid="mailbox-create-insufficient">
              <FieldError text={mailboxErrorText(insufficient.error, t, '')} tag="span" />
              {onTopUp ? (
                <div>
                  <Button label={t('Add funds to Main wallet')} variant="outline" onClick={onTopUp} />
                </div>
              ) : null}
            </FieldWrap>
          ) : null}

          {/* THE 409: the address exists already. No new password and no retry: the way forward is
              to rotate that mailbox's password, which the parent confirms (every mail client is
              signed out), then the new password shows here, once. */}
          {/* Shown while the refused address is typed, or once a rotation has answered (its password
              is on screen once and names its own address). */}
          {conflict && (conflictHolds || rotated) ? (
            <FieldWrap data-testid="mailbox-create-conflict">
              <FieldError text={conflict.error?.message ?? ''} tag="span" />
              {rotated ? (
                <CredentialBlock
                  address={conflict.email}
                  secret={rotated.password}
                  passwordSetAt={rotated.setAt}
                  onGenerate={noop}
                  onReset={onRotate ?? noop}
                  resetting={rotating}
                />
              ) : (
                <div>
                  <Button
                    label={rotating ? t('Changing…') : t('Change its password')}
                    variant="outline"
                    onClick={onRotate}
                    disabled={rotating || !onRotate}
                  />
                </div>
              )}
            </FieldWrap>
          ) : null}

          <PasswordEntry
            label={t('Password')}
            value={password}
            onChange={onPasswordChange}
            onRegenerate={regenerate}
            copyLabel={t('Copy password')}
            edited={edited}
            disabled={submitting}
            refusal={refusedPassword}
            hint
          />
        </Body>

        <FooterRow>
          <Button label={t('Cancel')} variant="text" onClick={onClose} disabled={submitting} />
          <Button
            label={submitting ? t('Creating…') : t('Create mailbox')}
            variant="primary"
            onClick={submit}
            disabled={submitting || !passwordReady || conflictHolds}
          />
        </FooterRow>
      </Form>
    </Panel>
  );
};

export default CreateMailboxView;
